import React, { useEffect, useMemo, useState } from "react";
import { Modal } from "../../../components/SystemComponent/Modal";
import { Button } from "../../../components/SystemComponent/Button";
import { createApi } from "../../../../core/api";

type Props = {
  open: boolean;
  type: "continent" | "country" | "city" | "airport";
  mode: "create" | "edit";
  data?: any;
  onClose: () => void;
  onSaved: () => void;
};

export const LocationCrudModal = ({
  open,
  type,
  mode,
  data,
  onClose,
  onSaved,
}: Props) => {
  const api = useMemo(() => createApi(), []);

  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [code2, setCode2] = useState("");
  const [saving, setSaving] = useState(false);

  const [continents, setContinents] = useState<any[]>([]);
  const [countries, setCountries] = useState<any[]>([]);
  const [cities, setCities] = useState<any[]>([]);

  const [continentId, setContinentId] = useState<number | "">("");
  const [countryId, setCountryId] = useState<number | "">("");
  const [cityId, setCityId] = useState<number | "">("");

  useEffect(() => {
    if (!open) return;

    api.continent.getAll().then((r) => {
      if (r?.ok) setContinents(r.data?.data ?? r.data ?? []);
    });

    api.country.getAll().then((r) => {
      if (r?.ok) setCountries(r.data?.data ?? r.data ?? []);
    });

    api.city.getAll().then((r) => {
      if (r?.ok) setCities(r.data?.data ?? r.data ?? []);
    });
  }, [open]);

  useEffect(() => {
    if (mode === "edit" && data) {
      setName(data.name ?? "");
      setCode(data.code ?? data.codeIATA ?? "");
      setCode2(data.code2 ?? "");

      if (type === "country") setContinentId(data.continentId);
      if (type === "city") setCountryId(data.countryId);
      if (type === "airport") setCityId(data.cityId);
    } else {
      setName("");
      setCode("");
      setCode2("");
      setContinentId("");
      setCountryId("");
      setCityId("");
    }
  }, [mode, data, open, type]);

  const save = async () => {
    if (saving) return;
    setSaving(true);

    const payload: any = { name };

    if (type === "continent") payload.code = code;

    if (type === "country") {
      payload.code = code;
      payload.code2 = code2;
      payload.continentId = continentId;
    }

    if (type === "city") {
      payload.code = code;
      payload.countryId = countryId;
    }

    if (type === "airport") {
      payload.codeIATA = code;
      payload.cityId = cityId;
    }

    if (mode === "create") await (api as any)[type].create(payload);
    else await (api as any)[type].update(data.id, payload);

    setSaving(false);
    onSaved();
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`${mode === "create" ? "Create" : "Update"} ${type}`}
    >
      <div style={S.form}>
        <div style={S.field}>
          <label style={S.label}>Name</label>
          <input
            style={S.input}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>

        {type !== "airport" && (
          <div style={S.field}>
            <label style={S.label}>Code</label>
            <input
              style={S.input}
              value={code}
              onChange={(e) => setCode(e.target.value)}
            />
          </div>
        )}

        {type === "airport" && (
          <div style={S.field}>
            <label style={S.label}>IATA Code</label>
            <input
              style={S.input}
              value={code}
              onChange={(e) => setCode(e.target.value)}
            />
          </div>
        )}

        {type === "country" && (
          <>
            <div style={S.field}>
              <label style={S.label}>Code 2</label>
              <input
                style={S.input}
                value={code2}
                onChange={(e) => setCode2(e.target.value)}
              />
            </div>

            <div style={S.field}>
              <label style={S.label}>Continent</label>
              <select
                style={S.input}
                value={continentId}
                onChange={(e) => setContinentId(Number(e.target.value))}
                disabled={mode === "edit"}
              >
                <option value="">Select continent</option>
                {continents.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
          </>
        )}

        {type === "city" && (
          <div style={S.field}>
            <label style={S.label}>Country</label>
            <select
              style={S.input}
              value={countryId}
              onChange={(e) => setCountryId(Number(e.target.value))}
              disabled={mode === "edit"}
            >
              <option value="">Select country</option>
              {countries.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {type === "airport" && (
          <div style={S.field}>
            <label style={S.label}>City</label>
            <select
              style={S.input}
              value={cityId}
              onChange={(e) => setCityId(Number(e.target.value))}
              disabled={mode === "edit"}
            >
              <option value="">Select city</option>
              {cities.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
        )}

        <div style={S.actions}>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={save} loading={saving}>
            Save
          </Button>
        </div>
      </div>
    </Modal>
  );
};

const S: Record<string, React.CSSProperties> = {
  form: { display: "flex", flexDirection: "column", gap: 14 },
  field: { display: "flex", flexDirection: "column", gap: 6 },
  label: { fontSize: 12, fontWeight: 700, color: "#334155" },
  input: {
    height: 42,
    borderRadius: 10,
    border: "1px solid #CBD5E1",
    padding: "0 14px",
    fontSize: 14,
  },
  actions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 10,
    marginTop: 20,
  },
};
